/**
 * Home do módulo de Inovação — dashboard acionável.
 * Não é "menu com links", é a caixa de entrada do GNI.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ClipboardCheck, FolderOpen, AlertTriangle, MessageSquare,
  FileEdit, Clock, CheckCircle, ArrowRight, Plus, TrendingUp,
  Calendar, Users, Sparkles, Briefcase, DollarSign, ListTree,
} from 'lucide-react';

import StatCard from '../../components/StatCard';
import MinhasAprovacoesPendentes from './MinhasAprovacoesPendentes';
import { rotuloEstadoProjeto } from './labels';
import {
  dashboardAPI, analisesAPI, projetosAPI, adminInovacaoAPI,
} from '../../services/inovacaoAPI';
import { useAuth } from '../../context/AuthContext';

const BRAND = 'var(--brand, #20a490)';

export default function InovacaoHome() {
  const { user } = useAuth();
  const [resumo, setResumo] = useState(null);
  const [rascunhos, setRascunhos] = useState([]);
  const [emRevisao, setEmRevisao] = useState([]);
  const [aguardando, setAguardando] = useState([]);
  const [projetosRisco, setProjetosRisco] = useState([]);
  const [projetosAtivos, setProjetosAtivos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function carregar() {
      try {
        const [r, rasc, rev, aguard, emRisco, ativos, evs] = await Promise.all([
          dashboardAPI.resumoGNI(),
          analisesAPI.listar({ estado: 'rascunho', size: 5 }),
          analisesAPI.listar({ estado: 'em_revisao_gni', size: 5 }),
          analisesAPI.listar({ estado: 'aguardando_respostas', size: 5 }),
          projetosAPI.listar({ estado: 'em_risco', size: 5 }),
          projetosAPI.listar({ estado: 'em_execucao', size: 6 }),
          adminInovacaoAPI.listarEventos({ size: 8 }).catch(() => ({ items: [] })),
        ]);
        setResumo(r);
        setRascunhos(rasc.items || []);
        setEmRevisao(rev.items || []);
        setAguardando(aguard.items || []);
        setProjetosRisco(emRisco.items || []);
        setProjetosAtivos(ativos.items || []);
        setEventos(evs.items || []);
      } catch (e) {
        toast.error('Erro ao carregar dashboard');
      } finally {
        setLoading(false);
      }
    }
    carregar();
  }, []);

  if (loading) return <div style={{ padding: 40 }}>Carregando…</div>;

  const nome = user?.nome_completo?.split(' ')[0] || 'Equipe';
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={estilos.hero}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: BRAND, fontSize: '0.85rem', fontWeight: 600 }}>
            <Sparkles size={14} /> GOVERNANÇA DE NOVAS INICIATIVAS
          </div>
          <h1 style={estilos.heroTitulo}>{saudacao}, {nome}.</h1>
          <p style={estilos.heroSubtitulo}>
            {contadorVivo(resumo, rascunhos, emRevisao, aguardando, projetosRisco)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/inovacao/templates" title="Templates de projeto"
                style={{ ...estilos.btnHero, background: '#fff', color: '#7f8c8d',
                         border: '1px solid #ddd', padding: '8px 12px' }}>
            <ListTree size={16} />
          </Link>
          <Link to="/inovacao/admin/custos-hora" title="Configurar R$/h por usuário"
                style={{ ...estilos.btnHero, background: '#fff', color: '#7f8c8d',
                         border: '1px solid #ddd', padding: '8px 12px' }}>
            <DollarSign size={16} />
          </Link>
          <Link to="/inovacao/meu-trabalho" style={{ ...estilos.btnHero, background: '#fff', color: BRAND, border: `1px solid ${BRAND}` }}>
            <Briefcase size={16} /> Meu trabalho
          </Link>
          <Link to="/inovacao/analises/nova" style={estilos.btnHero}>
            <Plus size={16} /> Nova análise
          </Link>
        </div>
      </div>

      {/* Aprovações pendentes do usuário (in-app, sem depender do e-mail) */}
      <MinhasAprovacoesPendentes />

      {/* KPIs */}
      <div style={estilos.gridStats}>
        <StatCard title="Análises ativas" value={resumo?.analises_ativas ?? 0}
                  icon={<ClipboardCheck size={20} />} color={BRAND}
                  onClick={() => window.location.href = '/inovacao/analises'} />
        <StatCard title="Aguardando aprovação" value={resumo?.analises_aguardando_aprovacao ?? 0}
                  icon={<Clock size={20} />} color="#f39c12"
                  onClick={() => window.location.href = '/inovacao/analises?estado=em_aprovacao'} />
        <StatCard title="Projetos em execução" value={resumo?.projetos_em_execucao ?? 0}
                  icon={<FolderOpen size={20} />} color="#3498db"
                  onClick={() => window.location.href = '/inovacao/projetos'} />
        <StatCard title="Projetos em risco" value={resumo?.projetos_em_risco ?? 0}
                  icon={<AlertTriangle size={20} />} color="#e74c3c" />
        <StatCard title="Conflitos abertos" value={resumo?.conflitos_abertos ?? 0}
                  icon={<AlertTriangle size={20} />} color="#e67e22"
                  onClick={() => window.location.href = '/inovacao/gantt'} />
        <StatCard title="Depoimentos pendentes" value={resumo?.retrospectivas_pendentes_depoimento ?? 0}
                  icon={<MessageSquare size={20} />} color="#9b59b6" />
      </div>

      {/* Grade principal */}
      <div style={estilos.gridPrincipal}>

        {/* Col 1: Caixa de entrada */}
        <div style={{ display: 'grid', gap: 16 }}>
          <CardAcao
            titulo="Rascunhos"
            descricao="Análises que você criou e ainda não convidou o operador"
            lista={rascunhos}
            vazio="Nada por aqui."
            icone={<FileEdit size={18} />}
            cor="#95a5a6"
            rotaItem={(a) => `/inovacao/analises/${a.id}`}
            rotaTodos="/inovacao/analises?estado=rascunho"
          />

          <CardAcao
            titulo="Com o operador"
            descricao="Aguardando respostas do questionário"
            lista={aguardando}
            vazio="Nenhuma análise aguardando respostas."
            icone={<Clock size={18} />}
            cor="#3498db"
            rotaItem={(a) => `/inovacao/analises/${a.id}`}
            rotaTodos="/inovacao/analises?estado=aguardando_respostas"
          />

          <CardAcao
            titulo="Para sua revisão"
            descricao="Operador respondeu: revise e envie para aprovação"
            lista={emRevisao}
            vazio="Sem análises na fila de revisão."
            icone={<CheckCircle size={18} />}
            cor="#9b59b6"
            rotaItem={(a) => `/inovacao/analises/${a.id}`}
            rotaTodos="/inovacao/analises?estado=em_revisao_gni"
          />
        </div>

        {/* Col 2: Projetos em foco */}
        <div style={{ display: 'grid', gap: 16 }}>
          {projetosRisco.length > 0 && (
            <div style={{ ...estilos.card, borderLeft: '4px solid #e74c3c' }}>
              <CardHeader icone={<AlertTriangle size={18} />} cor="#e74c3c"
                          titulo={`${projetosRisco.length} projeto(s) em risco`} />
              <div style={{ display: 'grid', gap: 8 }}>
                {projetosRisco.map((p) => (
                  <ProjetoMini key={p.id} p={p} />
                ))}
              </div>
            </div>
          )}

          <div style={estilos.card}>
            <CardHeader icone={<FolderOpen size={18} />} cor={BRAND}
                        titulo="Projetos em execução"
                        linkTodos="/inovacao/projetos?estado=em_execucao" />
            {projetosAtivos.length === 0 ? (
              <p style={{ color: '#bdc3c7', textAlign: 'center', margin: 12 }}>
                Nenhum projeto em execução.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {projetosAtivos.map((p) => <ProjetoMini key={p.id} p={p} />)}
              </div>
            )}
          </div>
        </div>

        {/* Col 3: Atividade + navegação secundária */}
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={estilos.card}>
            <CardHeader icone={<Calendar size={18} />} cor="#7f8c8d"
                        titulo="Atividade recente" />
            {eventos.length === 0 ? (
              <p style={{ color: '#bdc3c7', margin: 12 }}>Sem atividade recente.</p>
            ) : (
              <div style={{ position: 'relative', paddingLeft: 18 }}>
                <div style={{
                  position: 'absolute', left: 5, top: 6, bottom: 6,
                  width: 2, background: '#ecf0f1',
                }} />
                {eventos.map((e) => (
                  <div key={e.id} style={{ position: 'relative', marginBottom: 10 }}>
                    <div style={{
                      position: 'absolute', left: -17, top: 6,
                      width: 10, height: 10, borderRadius: 5,
                      background: e.processado_em ? BRAND : '#f39c12',
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 2px #ecf0f1',
                    }} />
                    <div style={{ fontSize: '0.8rem' }}>
                      <code style={{ color: BRAND, fontSize: '0.75rem' }}>{e.tipo}</code>
                      {e.ator && <span style={{ color: '#7f8c8d' }}> · {e.ator.nome}</span>}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>
                      {tempoRelativo(e.ocorrido_em)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={estilos.card}>
            <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem',
                         textTransform: 'uppercase', letterSpacing: 0.5,
                         color: '#95a5a6' }}>
              Também disponível
            </h3>
            <NavItem to="/inovacao/gantt" icone={<Calendar size={14} />} label="Gantt consolidado (reunião semanal)" />
            <NavItem to="/inovacao/carga-pessoas" icone={<Users size={14} />} label="Carga das pessoas (heatmap)" />
            <NavItem to="/inovacao/dashboard" icone={<TrendingUp size={14} />} label="Dashboard de apetite por setor" />
            <NavItem to="/inovacao/releases" icone={<Sparkles size={14} />} label="Releases mensais do GNI" />
            <NavItem to="/inovacao/templates" icone={<FileEdit size={14} />} label="Templates de projeto" />
            <NavItem to="/inovacao/admin" icone={<Users size={14} />} label="Eventos & jobs (admin)" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Sub-componentes ============

function CardAcao({ titulo, descricao, lista, vazio, icone, cor, rotaItem, rotaTodos }) {
  return (
    <div style={estilos.card}>
      <CardHeader icone={icone} cor={cor} titulo={`${titulo} (${lista.length})`}
                  linkTodos={lista.length > 0 ? rotaTodos : null} />
      <p style={{ color: '#7f8c8d', fontSize: '0.8rem', margin: '0 0 8px' }}>{descricao}</p>
      {lista.length === 0 ? (
        <p style={{ color: '#bdc3c7', fontSize: '0.85rem', margin: 12, textAlign: 'center' }}>{vazio}</p>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {lista.map((a) => (
            <Link key={a.id} to={rotaItem(a)} style={estilos.linhaAcao}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.88rem', color: '#2c3e50', fontWeight: 500,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.titulo}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#7f8c8d' }}>
                  {a.setor_solicitante?.nome} · {tempoRelativo(a.created_at)}
                </div>
              </div>
              {a.score_oportunidade != null && (
                <span style={{ background: corScore(a.score_oportunidade), color: '#fff',
                               padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem',
                               fontWeight: 600, marginRight: 8 }}>
                  {a.score_oportunidade}
                </span>
              )}
              <ArrowRight size={14} color="#bdc3c7" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CardHeader({ icone, cor, titulo, linkTodos }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
      <div style={{ background: `${cor}15`, color: cor, padding: 8,
                    borderRadius: 8, display: 'flex', alignItems: 'center' }}>
        {icone}
      </div>
      <h3 style={{ flex: 1, margin: '0 12px', fontSize: '1rem', color: '#2c3e50' }}>
        {titulo}
      </h3>
      {linkTodos && (
        <Link to={linkTodos} style={{ color: BRAND, fontSize: '0.8rem', textDecoration: 'none' }}>
          ver todos →
        </Link>
      )}
    </div>
  );
}

function ProjetoMini({ p }) {
  const est = rotuloEstadoProjeto(p.estado);
  return (
    <Link to={`/inovacao/projetos/${p.id}`} style={estilos.linhaAcao}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#2c3e50',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.nome}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#7f8c8d' }}>
          {p.responsavel_tecnico?.nome} · {p.data_fim_planejada
            ? `até ${new Date(p.data_fim_planejada).toLocaleDateString('pt-BR')}`
            : 'sem previsão'}
        </div>
      </div>
      <span style={{ background: est.cor, color: '#fff', padding: '2px 8px',
                     borderRadius: 10, fontSize: '0.7rem', marginRight: 8 }}>
        {est.label}
      </span>
      <ArrowRight size={14} color="#bdc3c7" />
    </Link>
  );
}

function NavItem({ to, icone, label }) {
  return (
    <Link to={to} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px',
      color: '#555', textDecoration: 'none', fontSize: '0.85rem',
      borderRadius: 4, transition: 'background 0.15s',
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
      <span style={{ color: BRAND, display: 'flex' }}>{icone}</span>
      <span style={{ flex: 1 }}>{label}</span>
      <ArrowRight size={12} color="#bdc3c7" />
    </Link>
  );
}

// ============ Helpers ============

function contadorVivo(resumo, rascunhos, emRevisao, aguardando, emRisco) {
  const itens = [];
  if (rascunhos.length) itens.push(`${rascunhos.length} rascunho${rascunhos.length > 1 ? 's' : ''}`);
  if (emRevisao.length) itens.push(`${emRevisao.length} aguardando revisão`);
  if (aguardando.length) itens.push(`${aguardando.length} com operador`);
  if (emRisco.length) itens.push(`${emRisco.length} projeto${emRisco.length > 1 ? 's' : ''} em risco`);
  if (!itens.length) return 'Tudo em dia por aqui.';
  return `Você tem ${itens.slice(0, -1).join(', ')}${itens.length > 1 ? ' e ' : ''}${itens.slice(-1)[0]}.`;
}

function tempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const agora = new Date();
  const min = Math.floor((agora - d) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const dias = Math.floor(h / 24);
  if (dias < 30) return `há ${dias}d`;
  return d.toLocaleDateString('pt-BR');
}

function corScore(n) {
  if (n >= 70) return '#27ae60';
  if (n >= 40) return '#f39c12';
  return '#95a5a6';
}

// ============ Estilos ============

const estilos = {
  hero: {
    background: '#fff', padding: '28px 32px', borderRadius: 12,
    marginBottom: 20, display: 'flex', alignItems: 'center',
    flexWrap: 'wrap', gap: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  heroTitulo: { margin: '8px 0 4px', color: '#2c3e50', fontSize: '1.8rem' },
  heroSubtitulo: { margin: 0, color: '#7f8c8d', fontSize: '0.95rem' },
  btnHero: {
    background: BRAND, color: '#fff', padding: '12px 22px',
    textDecoration: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 500,
    display: 'inline-flex', gap: 8, alignItems: 'center',
  },
  gridStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16, marginBottom: 20,
  },
  gridPrincipal: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16,
  },
  card: {
    background: '#fff', padding: 20, borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  linhaAcao: {
    display: 'flex', alignItems: 'center', padding: '10px 8px',
    borderRadius: 6, textDecoration: 'none',
    transition: 'background 0.15s',
    color: 'inherit',
  },
};
